<template>
  <div>
    <div class="col form-group french-address-form">
      <div class="section-row div-flex-row">
          <input
            ref="streetInput"
            v-model="obj.logradouro"
            class="w-100"
            :class="inputClass"
            :placeholder="translation.streetName"
            type="text"
          />
          <input
            v-model="obj.numero"
            class="numero"
            :class="inputClass"
            :placeholder="translation.houseNumber"
            type="text"
          />
        </div>

        <div class="section-row">
          <input
            v-model="obj.complement"
            class="mt-4 w-100"
            :class="inputClass"
            :placeholder="translation.addressLine2"
            type="text"
          />
        </div>

        <div class="section-row div-flex-row">
          <input
            ref="postalCodeInput"
            v-model="postalCode"
            class="mt-4 french-postal-code"
            :class="inputClass"
            :placeholder="translation.postalCode"
            type="text"
            autocomplete="postal-code"
            maxlength="10"
          />
          <input
            v-model="obj.localidade"
            class="mt-4 w-100"
            :class="inputClass"
            :placeholder="translation.city"
            type="text"
          />
        </div>

        <div v-if="isFrancePhone" class="section-row">
          <select
            v-model="obj.uf"
            class="uf mt-4 w-100 french-region-select"
            :class="[
              template.secondaryTheme ? 'secondary-input-theme' : 'c-input',
              { 'french-region-select--empty': !obj.uf },
            ]"
          >
            <option value="" disabled>{{ translation.stateRegionPlaceholder }}</option>
            <option
              v-for="region in frenchRegions"
              :key="region.code"
              :value="region.code"
            >
              {{ region.name }}
            </option>
          </select>
        </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import { loginFB } from '../../services/ServFB.js'
import { FRENCH_REGIONS } from '@/helpers/frenchRegions'
import { findCountryByIso3, isFranceRegistration } from '@/helpers/popularPhoneCountries'

const lFB = new loginFB()

export default {
  name: 'FrenchAddress',
  props: {
    validate: Boolean,
  },
  data: () => ({
    obj: {},
    postalCode: '',
    frenchRegions: FRENCH_REGIONS,
    toastMessage: '',
    isFrancePhone: false,
  }),
  computed: {
    ...mapState(['config', 'template', 'registrationObject', 'countries']),
    inputClass() {
      return this.template.secondaryTheme ? 'secondary-input-theme' : 'c-input'
    },
    phoneCountryIso3() {
      return String(
        this.obj.phoneCountryIso3 || this.registrationObject.phoneCountryIso3 || '',
      ).toUpperCase()
    },
    phoneCountryCode() {
      return String(
        this.obj.phoneCountryCode || this.registrationObject.phoneCountryCode || '',
      ).replace(/\D/g, '')
    },
    translation() {
      return {
        attention: this.$t('common.attention'),
        streetName: this.$t('frenchAddress.streetName'),
        houseNumber: this.$t('frenchAddress.houseNumber'),
        addressLine2: this.$t('frenchAddress.addressLine2'),
        postalCode: this.$t('frenchAddress.postalCode'),
        city: this.$t('frenchAddress.city'),
        stateRegionPlaceholder: this.$t('frenchAddress.stateRegionPlaceholder'),
        msgError: {
          streetName: this.$t('frenchAddress.streetNameRequired'),
          houseNumber: this.$t('frenchAddress.houseNumberRequired'),
          postalCode: this.$t('frenchAddress.postalCodeRequired'),
          city: this.$t('frenchAddress.cityRequired'),
          country: this.$t('frenchAddress.countryRequired'),
        },
      }
    },
  },
  watch: {
    validate(newValue) {
      if (newValue) this.validateFields()
    },
    registrationObject: {
      deep: true,
      handler(value) {
        this.refreshFrancePhoneState(value)
      },
    },
    isFrancePhone(isFrance) {
      if (!isFrance) {
        this.obj.uf = ''
      }
    },
  },
  mounted() {
    this.obj = JSON.parse(JSON.stringify(this.registrationObject))
    this.postalCode = this.obj.cep || ''
    if (this.obj.uf == null) {
      this.$set(this.obj, 'uf', '')
    }
    this.refreshFrancePhoneState(this.registrationObject)
    this.loadCountriesIfNeeded()

    if (this.$refs.streetInput) {
      this.$refs.streetInput.focus()
    }
  },
  methods: {
    ...mapMutations([
      'updateRegistrationObject',
      'clearRegistrationErrors',
      'addRegistrationError',
    ]),
    ...mapActions(['setCountries']),
    refreshFrancePhoneState(source = this.registrationObject) {
      this.isFrancePhone = isFranceRegistration(source)

      this.obj.phoneCountryIso3 = source.phoneCountryIso3 || this.obj.phoneCountryIso3 || ''
      this.obj.phoneCountryName = source.phoneCountryName || this.obj.phoneCountryName || ''
      this.obj.phoneCountryCode = source.phoneCountryCode || this.obj.phoneCountryCode || ''
      this.obj.phoneCountryIsFrance = this.isFrancePhone

      if (this.isFrancePhone && !this.obj.phoneCountryCode) {
        this.obj.phoneCountryCode = '33'
        this.obj.phoneCountryIso3 = 'FRA'
      }
    },
    loadCountriesIfNeeded() {
      if (this.countries.length) {
        this.resolveCountryId()
        return
      }

      lFB
        .GetCountries()
        .then(data => {
          this.setCountries(data)
          this.resolveCountryId()
        })
        .catch(() => {})
    },
    resolveCountryId() {
      const iso3 = this.phoneCountryIso3
      if (!iso3 || !this.countries.length) return

      const country = findCountryByIso3(this.countries, iso3)
      if (country) {
        this.obj.country = country.id
      }
    },
    makeToast(variant = null) {
      this.$bvToast.toast(this.toastMessage, {
        title: this.translation.attention,
        variant,
        solid: true,
      })
    },
    validateFields() {
      this.$emit('validating')
      this.clearRegistrationErrors()

      let hasErrors = false

      if (!this.obj.logradouro || !this.obj.logradouro.trim()) {
        this.addRegistrationError({ msg: this.translation.msgError.streetName })
        this.toastMessage = this.translation.msgError.streetName
        this.makeToast('warning')
        hasErrors = true
      }

      if (!this.obj.numero || !String(this.obj.numero).trim()) {
        this.addRegistrationError({ msg: this.translation.msgError.houseNumber })
        this.toastMessage = this.translation.msgError.houseNumber
        this.makeToast('warning')
        hasErrors = true
      }

      this.obj.cep = String(this.postalCode || '').trim()

      if (!this.obj.cep) {
        this.addRegistrationError({ msg: this.translation.msgError.postalCode })
        this.toastMessage = this.translation.msgError.postalCode
        this.makeToast('warning')
        hasErrors = true
      }

      if (!this.obj.localidade || !this.obj.localidade.trim()) {
        this.addRegistrationError({ msg: this.translation.msgError.city })
        this.toastMessage = this.translation.msgError.city
        this.makeToast('warning')
        hasErrors = true
      }

      if (!this.obj.country && !this.phoneCountryIso3) {
        this.addRegistrationError({ msg: this.translation.msgError.country })
        this.toastMessage = this.translation.msgError.country
        this.makeToast('warning')
        hasErrors = true
      }

      if (!hasErrors) {
        this.obj.bairro = ''
        this.obj.phoneCountryIso3 = this.phoneCountryIso3

        if (!this.isFrancePhone) {
          this.obj.uf = ''
        }

        this.resolveCountryId()
        this.$store.commit('updateRegistrationObject', this.obj)
        this.$emit('next')
      }
    },
  },
}
</script>

<style scoped>
.french-address-form {
  margin-bottom: 0;
}

.numero {
  width: 35%;
  min-width: 90px;
}

.uf {
  position: relative;
  padding: 4px 0;
  z-index: 1;
  appearance: auto;
  -webkit-appearance: menulist;
}

.french-region-select--empty {
  opacity: 0.7;
}

.french-region-select option {
  color: #222 !important;
  background-color: #fff !important;
}

.div-flex-row {
  display: flex;
  gap: 16px;
}

.section-row {
  width: 100%;
  max-width: 330px;
  margin: 0 auto;
  position: relative;
  z-index: 0;
}
</style>
